import { Component, Prop, Vue } from 'vue-property-decorator';
import ErrorHandler from '@/support/ErrorHandler';
import { Size } from '@/models/Size';
import { Color } from '@/models/Color';
import { ProductVariant } from '@/models/ProductVariant';
import { TableVisibility } from '@/components/data-table/DataTable';

@Component<CopyToVariantDialog>({})
export default class CopyToVariantDialog extends Vue {
  //#region @Props
  /**
  * ie.
  * @Prop()
  * protected user!: User
  */

  @Prop()
  protected variant!: ProductVariant;
  //#endregion


  //#region @Refs
  /**
  * ie.
  * @Ref()
  * readonly anotherComponent!: AnotherComponent
  *
  //#endregion


  //#region Class properties
  /**
  * ie.
  * protected isLoading = true;
  * * protected company: Company | null = null;
  */

  protected isLoading = false;
  protected currentStep = 1;

  protected selectedVariants: ProductVariant[] = [];
  protected selectedOptions: string[] = [];

  protected visibility: TableVisibility = {
    checkboxes: true,
    limit: false,
    search: false,
    title: false,
    total: false,
    pagination: false,
    scrollable: true,
  };
  //#endregion


  //#region Lifecycle Hooks / Init
  /**
  * ie.
  * protected mounted(): void {
  *  console.log('MyComponent has Mounted');
  * }
  */

  protected mounted() {
    this.initialize();
  }
  //#endregion


  //#region Class methods
  /**
  * ie.
  * protected toggleLoading(): void {
  *  this.isLoading = ! this.isLoading;
  * }
  */

  protected async initialize() {
    //
  }


  protected close(): void {
    this.$emit('input', false);
  }


  //#endregion


  //#region Async methods
  /**
  * ie.
  * protected async fetchUserCompany(): Promise<void> {
  *  this.company = await new Company().filter({user: this.user.id}).all();
  * }
  */

  protected async create(): Promise<void> {
    this.isLoading = true;

    try {
      const productVariantPromises = this.selectedVariants.map((variant) => this.updateVariant(variant));
      await Promise.all(productVariantPromises);

      this.$emit('created');
      this.close();
    } catch (error) {
      ErrorHandler.network(error);
    }

    this.isLoading = false;
  }

  protected async updateVariant(variant: ProductVariant): Promise<any> {
    return new ProductVariant({id: variant.id}).update(this.payload);
  }
  //#endregion


  //#region Getters & Setters
  /**
  * ie.
  * protected get companyName(): string {
  *  return this?.company.name || 'N/A';
  * }
  */

  protected get payload() {
    const payload: Record<string, string | number> = {};

    this.selectedOptions.forEach(option => {
      const foundOption = (this.variant as any)[option];
      if (typeof foundOption === 'object' && foundOption.id) {
        // if foundOption is a object with id;
        payload[option] = foundOption.id;
      } else {
        payload[option] = foundOption;
      }
    });

    return payload;
  }

  get tableOptions() {
    return {
      model: new ProductVariant().filter({product: this.variant.product_uuid, exclude: this.variant.id}),
      name: {
        singular: 'Variant',
        plural: 'Varianten',
      },
      headers: [
        {
          text: 'Name',
          value: 'name',
        },
        {
          text: 'SKU',
          value: 'sku',
        },
        {
          text: 'Color',
          value: 'color',
          transform: (color: Color) => {
            return color ? color.name : '';
          }
        },
        {
          text: 'Size',
          value: 'size',
          transform: (size: Size) => {
            return size ? size.name : '';
          }
        },
      ],
    };
  }


  protected get steps(): Step[] {
    return [
      {
        title: 'Variant',
      },
      {
        title: 'Varianten',
      },
    ];
  }
  //#endregion


  //#region @Watchers
  /**
  * ie.
  * @Watch('company')
  * protected companyChanged() {
  *  console.log('Company Changed') ;
  * }
  */
  //#endregion
}

interface Step {
  title: string;
}
